import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronDown, Eye, Undo2 } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/useToast';
import { roleLabel } from '@/lib/badges';
import { cuentasParaVerComo, entrarComo, viendoComo, volverAMiCuenta } from '@/lib/ver-como';

/** En el menú de la cuenta: las otras cuentas del negocio, para entrar sin cerrar la sesión. */
export function MenuVerComo() {
  const { user } = useAuth();
  const toast = useToast();
  const [entrando, setEntrando] = useState<string | null>(null);
  const esAdministracion = user?.role === 'OWNER' && !viendoComo();
  const { data } = useQuery({
    queryKey: ['ver-como', 'cuentas'],
    queryFn: cuentasParaVerComo,
    enabled: esAdministracion,
  });
  if (!esAdministracion || !data?.length) return null;
  return (
    <div className="border-b border-surface-border py-1 dark:border-slate-800">
      <p className="px-3 pb-1 pt-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-400">Entrar como</p>
      <div className="max-h-64 overflow-auto">
        {data.map((c) => (
          <button
            key={c.id}
            type="button"
            disabled={entrando !== null}
            onClick={() => {
              setEntrando(c.id);
              entrarComo(c.id).catch((e: unknown) => {
                setEntrando(null);
                toast.error(e instanceof Error ? e.message : 'No se pudo entrar a esa cuenta');
              });
            }}
            className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800"
          >
            <Eye className="h-4 w-4 shrink-0" />
            <span className="min-w-0 flex-1 truncate">{c.nombre}</span>
            <span className="shrink-0 text-xs text-slate-400">{roleLabel[c.rol as keyof typeof roleLabel] ?? c.rol}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

/** Arriba de todo mientras se mira otra cuenta: de quién es y cómo volver. */
export function FranjaVerComo() {
  const { user } = useAuth();
  if (!viendoComo()) return null;
  return (
    <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 bg-amber-400 px-4 py-2 text-center text-sm font-medium text-amber-950">
      <span>
        Estás viendo como <strong>{user?.name ?? user?.email}</strong>. Lo que escribas queda con tu nombre.
      </span>
      <button
        type="button"
        onClick={() => volverAMiCuenta()}
        className="inline-flex items-center gap-1 rounded-full bg-amber-950 px-3 py-1 text-xs font-semibold text-amber-50 hover:bg-amber-900"
      >
        <Undo2 className="h-3.5 w-3.5" />
        Volver a mi cuenta
      </button>
    </div>
  );
}

/**
 * Para las pantallas sin menú de cuenta (Mi Conversia de un cliente reflejo o VIP): un botón
 * «Entrar como» que abre el mismo desplegable.
 */
export function BotonVerComo() {
  const { user } = useAuth();
  const [abierto, setAbierto] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const esAdministracion = user?.role === 'OWNER' && !viendoComo();
  const { data } = useQuery({
    queryKey: ['ver-como', 'cuentas'],
    queryFn: cuentasParaVerComo,
    enabled: esAdministracion,
  });
  useEffect(() => {
    const fuera = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setAbierto(false);
    };
    document.addEventListener('mousedown', fuera);
    return () => document.removeEventListener('mousedown', fuera);
  }, []);
  if (!esAdministracion || !data?.length) return null;
  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setAbierto((v) => !v)}
        aria-expanded={abierto}
        className="inline-flex items-center gap-1.5 rounded-md border border-surface-border px-2.5 py-1.5 text-sm text-slate-600 hover:bg-slate-100 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
      >
        <Eye className="h-4 w-4" />
        <span className="hidden sm:inline">Entrar como</span>
        <ChevronDown className="h-3.5 w-3.5" />
      </button>
      {abierto && (
        <div className="absolute right-0 z-50 mt-1 w-64 max-w-[calc(100vw-2rem)] rounded-lg border border-surface-border bg-white p-1 shadow-lg dark:border-slate-800 dark:bg-slate-900">
          <MenuVerComo />
        </div>
      )}
    </div>
  );
}
